import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {CoreDI, Device, DeviceType, inject, ScreenManager, Installer, ModuleIsEnabledFilter} from '@gongxigames/framework/core';
import {createScrollToFullscreen, IScrollToFullscreen} from '@gongxigames/framework/slot/modules/iOSFullscreen/scrollToFullscreen';
import {FullscreenState} from '@gongxigames/framework/slot/modules/iOSFullscreen/states/FullscreenState';
import {IdleState} from '@gongxigames/framework/slot/modules/iOSFullscreen/states/IdleState';
import {NotFullscreenState} from '@gongxigames/framework/slot/modules/iOSFullscreen/states/NotFullscreenState';

export enum IOSFullscreenModuleStates {
    NotFullScreen = 'NotFullScreen',
    Fullscreen = 'Fullscreen',
    Idle = 'Idle',
}

export class IOSFullscreenModule extends Module<null, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('IOSFullscreenModule');
    public static platform: DeviceType = DeviceType.MOBILE;
    public static readonly isEnabled: ModuleIsEnabledFilter = (components: Installer) => {
        const device = components.getComponent<Device>(CoreDI.Device);

        return Boolean(IOSFullscreenModule.isTargetDevice(device));
    };

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(CoreDI.ScreenManager)
    protected _screenManager!: ScreenManager;

    private _scroll: IScrollToFullscreen | undefined;

    public static isTargetDevice(device: Device): boolean {
        const {mobileSafari, fullscreen, isInIframe, iOS} = device;

        return iOS && (mobileSafari && !fullscreen) && !isInIframe;
    }

    public init(): void {
        this._scroll = createScrollToFullscreen(this._device);

        super.init();
    }

    public destroy(): void {
        super.destroy();

        if (this._scroll) {
            this._scroll.destroy();
        }
    }

    public showScroll(): void {
        const {orientation} = this._screenManager.getScreenState();

        if (this._scroll) {
            this._scroll.show(orientation);
        }
    }

    public hideScroll(): void {
        const {orientation} = this._screenManager.getScreenState();

        if (this._scroll) {
            this._scroll.hide(orientation);
        }
    }

    public isFullScreen(): boolean {
        const {orientation} = this._screenManager.getScreenState();

        if (this._scroll) {
            return this._scroll.isFullscreen(orientation);
        }

        return false;
    }

    protected setupStates(): void {
        this.addState(new FullscreenState(this, IOSFullscreenModuleStates.Fullscreen));
        this.addState(new NotFullscreenState(this, IOSFullscreenModuleStates.NotFullScreen));
        this.addState(new IdleState(this, IOSFullscreenModuleStates.Idle));

        if (this.isFullScreen()) {
            this.gotoState(IOSFullscreenModuleStates.Fullscreen);
        } else {
            this.gotoState(IOSFullscreenModuleStates.NotFullScreen);
        }
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.onCurrentStateHandle('General.Popups.Add');
        this.onCurrentStateHandle('Autoplay.HIDE_OPTIONS_POPUP');
        this.onCurrentStateHandle('General.Popups.AllPopupsHidden');
        this.onCurrentStateHandle('General.Screen.RESIZED');

        this.on('Autoplay.SHOW_OPTIONS_POPUP', this.goToIdleState.bind(this));
    }

    protected setupViews(): void {}

    private goToIdleState(): void {
        this.gotoState(IOSFullscreenModuleStates.Idle);
    }
}
